import React from "react";
import { motion } from "framer-motion";
import { Code, Palette, Database, Layers, Wrench, Brain } from "lucide-react";

// Skill items component
function SkillItems({ items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <motion.span
          key={idx}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: idx * 0.1 }}
          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white/80 text-sm hover:bg-orange-500/20 hover:border-orange-500/50 transition-all"
        >
          {item}
        </motion.span>
      ))}
    </div>
  );
}

export function BentoGrid({ skillsData }) {
  // Use provided skills data or fallback to default
  const categories = skillsData?.categories || [
    { name: "FRONTEND", items: ["JavaScript","TypeScript", "React", "Vue.js","Angular","HTML/CSS", "Tailwind CSS","SASS", "SCSS"] },
    { name: "BACKEND", items: ["PHP", "Laravel", ".NET", "Node.js", "C#"] },
    { name: "DATABASE", items: ["MySQL", "MongoDB", "PostgreSQL", "SQL Server"] },
    { name: "DESIGN", items: ["Figma", "Adobe XD", "UI/UX Design", "Prototyping"] },
    { name: "TOOLS", items: ["Git", "GitHub", "Jira", "VS Code", "Docker","SonarQube","Qlik Sense"] },
  ];

  return (
    <section className="bg-[#0a0a0a] px-6 py-24 min-h-screen flex items-center justify-center">
      <div className="max-w-7xl w-full mx-auto">
        <motion.p
          className="text-orange-500 text-sm uppercase tracking-widest mb-4 font-silkscreen"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Skills & Expertise
        </motion.p>

        <motion.h2
          className="text-3xl lg:text-4xl  mb-8 font-silkscreen text-white"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          Technologies I Worked With
        </motion.h2>

        <motion.p
          className="text-white/60 text-lg mb-12 max-w-2xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          A comprehensive toolkit to bring ideas to life and create exceptional digital experiences.
        </motion.p>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-[200px]">

          {/* 1. FRONTEND - Tall (2x2) */}
          <motion.div
            className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-xl p-6 flex flex-col hover:border-orange-500/50 transition-colors overflow-hidden group"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Code className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-xl text-white font-medium font-silkscreen">FRONTEND</h4>
                <p className="text-gray-400 text-xs">Client-side Development</p>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <SkillItems items={categories.find(c => c.name === "FRONTEND")?.items || []} />
            </div>
          </motion.div>

          {/* 2. BACKEND - Tall (2x2) */}
          <motion.div
            className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-xl p-6 flex flex-col hover:border-orange-500/50 transition-colors overflow-hidden group"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-xl text-white font-medium font-silkscreen">BACKEND</h4>
                <p className="text-gray-400 text-xs">Server-side Logic</p>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <SkillItems items={categories.find(c => c.name === "BACKEND")?.items || []} />
            </div>
          </motion.div>

          {/* 3. DATABASE - Tall (2x2) */}
          <motion.div
            className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-xl p-6 flex flex-col hover:border-orange-500/50 transition-colors overflow-hidden group"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="text-xl text-white font-medium font-silkscreen">DATABASE</h4>
                <p className="text-gray-400 text-xs">Data Management</p>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <SkillItems items={categories.find(c => c.name === "DATABASE")?.items || []} />
            </div>
          </motion.div>

          {/* 5. TOOLS - Wide (3x1) */}
          <motion.div
            className="md:col-span-3 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-xl p-6 flex flex-col hover:border-orange-500/50 transition-colors overflow-hidden group"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 0.98 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg text-white font-medium font-silkscreen">TOOLS</h4>
                <p className="text-gray-400 text-xs">Development Workflow</p>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <SkillItems items={categories.find(c => c.name === "TOOLS")?.items || []} />
            </div>
          </motion.div>

          {/* 6. DESIGN - Wide (3x1) */}
          <motion.div
            className="md:col-span-3 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-xl p-6 flex flex-col hover:border-orange-500/50 transition-colors overflow-hidden group"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 0.98 }}
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg text-white font-medium font-silkscreen">DESIGN</h4>
                <p className="text-gray-400 text-xs">UI/UX Creation</p>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <SkillItems items={categories.find(c => c.name === "DESIGN")?.items || []} />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
